import { useMemo, useState } from 'react';
import { useOrgFilter, pickDefaultOrg } from '@/store/defaults';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { GridMoreRightButton, useAgMoreRight } from '@/shared/components/GridMoreRight';
import type { ColDef } from 'ag-grid-community';
import {
  reports,
  type GoodsBalanceRow,
  type GoodsBatchRow,
} from '@/api/documents';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';
import DatePopover from '@/components/DatePopover';

function endOfToday(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 0);
  const tz = -d.getTimezoneOffset();
  const sign = tz >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(tz / 60)}:${pad(tz % 60)}`;
}

const fmt = (v: string | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

export default function GoodsBalancePage() {
  const { t } = useTranslation();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  const [whId, setWhId] = useState<string>('');
  const [onDate, setOnDate] = useState<string>(() => endOfToday());
  const [view, setView] = useState<'summary' | 'batches'>('summary');

  // Персист ширин колонок — окремий ключ на кожну сітку (різні колонки).
  const colWSummary = useGridColumnWidths('goods-balance-summary');
  const colWBatches = useGridColumnWidths('goods-balance-batches');
  // ч.1316: «ще колонки →» над обома таблицями.
  const moreSummary = useAgMoreRight<GoodsBalanceRow>();
  const moreBatches = useAgMoreRight<GoodsBatchRow>();

  // выставить организацию по умолчанию
  if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);

  const warehousesQ = useQuery({
    queryKey: ['warehouses-bal', orgId],
    queryFn: () => refs.warehouses(orgId),
    enabled: !!orgId,
  });

  const summary = useQuery({
    queryKey: ['goods-balance', orgId, whId, onDate],
    queryFn: () => reports.goodsBalance({
      organization_id: orgId,
      on_date: onDate,
      warehouse_id: whId || undefined,
    }),
    enabled: !!orgId && view === 'summary',
  });

  const batches = useQuery({
    queryKey: ['goods-batches', orgId, onDate],
    queryFn: () => reports.goodsBatches({ organization_id: orgId, on_date: onDate }),
    enabled: !!orgId && view === 'batches',
  });

  const summaryCols = useMemo<ColDef<GoodsBalanceRow>[]>(() => [
    { field: 'warehouse_code', headerName: t('gb.warehouse_code'), width: 110 },
    { field: 'warehouse_name', headerName: t('gb.warehouse'), width: 220 },
    { field: 'nomenclature_code', headerName: t('gb.code'), width: 120 },
    { field: 'nomenclature_article', headerName: t('gb.article'), width: 100 },
    { field: 'nomenclature_name', headerName: t('gb.name'), flex: 1, minWidth: 250 },
    {
      field: 'quantity',
      headerName: t('gb.quantity'),
      width: 130,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmtQty(p.value as string),
    },
    {
      field: 'cost',
      headerName: t('gb.cost'),
      width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmt(p.value as string | null),
    },
  ], [t]);

  const batchCols = useMemo<ColDef<GoodsBatchRow>[]>(() => [
    { field: 'warehouse_name', headerName: t('gb.warehouse'), width: 200 },
    { field: 'nomenclature_code', headerName: t('gb.code'), width: 120 },
    { field: 'nomenclature_name', headerName: t('gb.name'), flex: 1, minWidth: 240 },
    {
      field: 'receipt_date',
      headerName: t('gb.batch_date'),
      width: 130,
      valueFormatter: (p) => p.value ? new Date(p.value as string).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'cost_price',
      headerName: t('gb.unit_cost'),
      width: 130,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmt(p.value as string),
    },
    {
      field: 'quantity',
      headerName: t('gb.quantity'),
      width: 120,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmtQty(p.value as string),
    },
    {
      field: 'cost',
      headerName: t('gb.cost'),
      width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => fmt(p.value as string),
    },
  ], [t]);

  const totals = summary.data?.totals;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('gb.title')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className={view === 'summary' ? 'primary' : ''}
            onClick={() => setView('summary')}
          >
            {t('gb.view_summary')}
          </button>
          <button
            className={view === 'batches' ? 'primary' : ''}
            onClick={() => setView('batches')}
          >
            {t('gb.view_batches')}
          </button>
          <button onClick={() => view === 'summary' ? summary.refetch() : batches.refetch()}>
            {t('tb.refresh')}
          </button>
        </div>
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{t('tb.organization')}</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
        </div>
        {view === 'summary' && (
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>{t('gb.warehouse')}</label>
            <select value={whId} onChange={(e) => setWhId(e.target.value)}>
              <option value="">{t('gb.all_warehouses')}</option>
              {warehousesQ.data?.map((w) => (
                <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
              ))}
            </select>
          </div>
        )}
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{t('gb.on_date')}</label>
          <DatePopover
            withTime
            value={onDate.slice(0, 16)}
            onChange={(v) => setOnDate(v)}
          />
        </div>
      </div>

      {view === 'summary' ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}><GridMoreRightButton more={moreSummary} /></div>
          <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 380px)', minHeight: 400, marginTop: '0.25rem' }}>
            <AgGridReact<GoodsBalanceRow>
              rowData={summary.data?.rows ?? []}
              columnDefs={summaryCols}
              loading={summary.isLoading}
              {...moreSummary.gridProps}
              onColumnResized={(e) => { colWSummary.onColumnResized(e); moreSummary.check(e.api); }}
              onGridReady={(e) => { colWSummary.applyTo(e.api); moreSummary.gridProps.onGridReady(e); }}
            />
          </div>
          {totals && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem', fontWeight: 600, gap: '2rem' }}>
              <span>{t('gb.total_qty')}: {fmtQty(totals.quantity)}</span>
              <span>{t('gb.total_cost')}: {fmt(totals.cost)}</span>
            </div>
          )}
        </>
      ) : (
        <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}><GridMoreRightButton more={moreBatches} /></div>
        <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 320px)', minHeight: 400, marginTop: '0.25rem' }}>
          <AgGridReact<GoodsBatchRow>
            rowData={batches.data?.rows ?? []}
            columnDefs={batchCols}
            loading={batches.isLoading}
            {...moreBatches.gridProps}
            onColumnResized={(e) => { colWBatches.onColumnResized(e); moreBatches.check(e.api); }}
            onGridReady={(e) => { colWBatches.applyTo(e.api); moreBatches.gridProps.onGridReady(e); }}
          />
        </div>
        </>
      )}
    </div>
  );
}
